import { useState } from "react";
import { siteInfo } from "@/mocks/site";
import { usePageMeta } from "@/hooks/usePageMeta";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db78g0mn6nb9f68e9aqg";

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

const blocks = [
  {
    icon: "ri-focus-3-line",
    title: "网站定位",
    text: siteInfo.intro
  },
  {
    icon: "ri-stack-line",
    title: "内容范围",
    text: siteInfo.scope
  },
  {
    icon: "ri-shield-check-line",
    title: "使用说明",
    text: "秘密影院致力于提供规范、清晰的影视信息浏览体验。本站内容为影视展示与推荐演示，不提供真实在线播放、下载或会员付费服务，所有影视信息均为演示数据。"
  }
];

export default function About() {
  usePageMeta({
    title: "关于我们 - 秘密影院",
    description:
      "了解秘密影院的网站定位、内容范围与联系方式。秘密影院是专注于电影、电视剧等内容展示与推荐的影视平台，欢迎通过留言反馈与我们联系。",
    keywords: "关于秘密影院,秘密影院介绍,影视平台,联系我们,用户反馈"
  });

  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const honeypot = String(formData.get("website_alt") || "").trim();

    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      setCharCount(0);
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      payload.append(key, String(value));
    });

    setStatus("submitting");
    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString()
      });
      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        "";
      const isSpam =
        typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setFormError("");
        form.reset();
        setCharCount(0);
      } else {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg
            ? serverMsg
            : "提交失败，请稍后重试"
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试");
    }
  };

  return (
    <div className="w-full px-4 py-10 md:px-6 md:py-14">
      <header className="mx-auto max-w-4xl text-center">
        <span className="inline-flex items-center gap-2 rounded-full bg-primary-500/40 px-4 py-1 text-xs text-secondary-300">
          <span className="flex h-3.5 w-3.5 items-center justify-center">
            <i className="ri-information-line" aria-hidden="true" />
          </span>
          关于我们
        </span>
        <h1 className="mt-5 font-heading text-3xl font-800 text-foreground-50 md:text-4xl">
          关于秘密影院
        </h1>
        <p className="mx-auto mt-4 max-w-2xl text-sm leading-7 text-foreground-400 md:text-base">
          {siteInfo.slogan}，让查找与浏览影视内容都变得轻松愉快。
        </p>
      </header>

      <div className="mx-auto mt-12 grid max-w-5xl gap-5 md:grid-cols-3">
        {blocks.map((block) => (
          <section
            key={block.title}
            className="rounded-lg border border-background-800 bg-background-900/60 p-6"
          >
            <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-500/15 text-accent-400">
              <i className={`${block.icon} text-xl`} aria-hidden="true" />
            </span>
            <h2 className="mt-4 font-heading text-lg font-600 text-foreground-50">
              {block.title}
            </h2>
            <p className="mt-2 text-sm leading-7 text-foreground-400">{block.text}</p>
          </section>
        ))}
      </div>

      <div className="mx-auto mt-6 max-w-5xl rounded-lg border border-background-800 bg-background-900/60 p-6 md:p-8">
        <h2 className="flex items-center gap-3 font-heading text-lg font-700 text-foreground-50">
          <span className="h-5 w-1 rounded-full bg-accent-500" aria-hidden="true" />
          联系方式
        </h2>
        <div className="mt-4 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-foreground-300">
          <span className="flex items-center gap-2">
            <span className="flex h-4 w-4 items-center justify-center text-secondary-400">
              <i className="ri-mail-line" aria-hidden="true" />
            </span>
            {siteInfo.email}
          </span>
          <span className="flex items-center gap-2">
            <span className="flex h-4 w-4 items-center justify-center text-secondary-400">
              <i className="ri-time-line" aria-hidden="true" />
            </span>
            我们会在 1-2 个工作日内回复
          </span>
        </div>
      </div>

      <div className="mx-auto mt-6 max-w-5xl rounded-lg border border-background-800 bg-background-900/60 p-6 md:p-8">
        <h2 className="flex items-center gap-3 font-heading text-lg font-700 text-foreground-50">
          <span className="h-5 w-1 rounded-full bg-accent-500" aria-hidden="true" />
          意见反馈与留言
        </h2>
        <p className="mt-2 text-sm text-foreground-400">
          对网站内容、功能或体验有任何建议，欢迎留言告诉我们。
        </p>

        <form
          id="feedback-form"
          data-readdy-form
          onSubmit={handleSubmit}
          className="mt-6 space-y-4"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor="feedback-name"
                className="mb-1.5 block text-sm text-foreground-300"
              >
                你的称呼
              </label>
              <input
                id="feedback-name"
                name="name"
                type="text"
                required
                placeholder="请输入称呼"
                className="w-full rounded-md border border-background-700 bg-background-950/60 px-4 py-2.5 text-sm text-foreground-100 placeholder:text-foreground-500 focus:border-secondary-500 focus:outline-none"
              />
            </div>
            <div>
              <label
                htmlFor="feedback-email"
                className="mb-1.5 block text-sm text-foreground-300"
              >
                联系邮箱
              </label>
              <input
                id="feedback-email"
                name="email"
                type="email"
                required
                placeholder="name@example.com"
                className="w-full rounded-md border border-background-700 bg-background-950/60 px-4 py-2.5 text-sm text-foreground-100 placeholder:text-foreground-500 focus:border-secondary-500 focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="feedback-message"
              className="mb-1.5 flex items-center justify-between text-sm text-foreground-300"
            >
              <span>反馈内容</span>
              <span className="text-xs text-foreground-500">{charCount}/500</span>
            </label>
            <textarea
              id="feedback-message"
              name="message"
              required
              maxLength={500}
              rows={5}
              onChange={(e) => setCharCount(e.target.value.length)}
              placeholder="请描述你的建议或遇到的问题（不超过 500 字）"
              className="w-full resize-none rounded-md border border-background-700 bg-background-950/60 px-4 py-2.5 text-sm text-foreground-100 placeholder:text-foreground-500 focus:border-secondary-500 focus:outline-none"
            />
          </div>

          <div className="feedback-hp-wrap" aria-hidden="true">
            <label htmlFor="feedback-website-alt">请勿填写此字段</label>
            <input
              id="feedback-website-alt"
              name="website_alt"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />
          </div>

          {status === "error" && formError && (
            <p className="flex items-center gap-2 rounded-md bg-accent-500/15 px-4 py-2.5 text-sm text-accent-200">
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-error-warning-line" aria-hidden="true" />
              </span>
              {formError}
            </p>
          )}

          {status === "success" && (
            <p className="flex items-center gap-2 rounded-md bg-secondary-500/15 px-4 py-2.5 text-sm text-secondary-200">
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-checkbox-circle-line" aria-hidden="true" />
              </span>
              感谢你的反馈，我们已经收到啦！
            </p>
          )}

          <button
            type="submit"
            disabled={status === "submitting"}
            className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-2.5 text-sm font-700 text-background-950 transition-colors hover:bg-accent-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-send-plane-line" aria-hidden="true" />
            </span>
            {status === "submitting" ? "提交中…" : "提交反馈"}
          </button>
        </form>
      </div>
    </div>
  );
}